import * as React from "react";
import { Pressable, Text, View, type PressableProps } from "react-native";
import Animated from "react-native-reanimated";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { cn } from "@/shared/lib/utils";
import { usePressableScale } from "@/shared/hooks/use-pressable-scale";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

type ListRowProps = Omit<PressableProps, "children"> & {
  leadingIcon?: React.ComponentProps<typeof Ionicons>["name"];
  tone?: "neutral" | "pink";
  label: string;
  description?: string;
  trailing?: React.ReactNode;
  showSeparator?: boolean;
  className?: string;
};

export function ListRow({
  leadingIcon,
  tone = "neutral",
  label,
  description,
  trailing,
  showSeparator = true,
  className,
  disabled,
  onPressIn: callerPressIn,
  onPressOut: callerPressOut,
  onPress,
  accessibilityRole = "button",
  ...props
}: ListRowProps) {
  const { onPressIn, onPressOut, animatedStyle } = usePressableScale(0.98);
  const [foreground, mutedForeground] = useCSSVariable([
    "--color-foreground",
    "--color-muted-foreground",
  ]) as [string, string];

  const isPink = tone === "pink";

  return (
    <AnimatedPressable
      onPress={onPress}
      onPressIn={(e) => {
        if (!disabled) onPressIn();
        callerPressIn?.(e);
      }}
      onPressOut={(e) => {
        if (!disabled) onPressOut();
        callerPressOut?.(e);
      }}
      disabled={disabled}
      accessibilityRole={accessibilityRole}
      className={cn(
        "flex-row items-center gap-3 py-3",
        showSeparator && "border-border/40 border-b",
        disabled && "opacity-50",
        className
      )}
      style={animatedStyle}
      {...props}
    >
      {leadingIcon && (
        <View
          className={cn(
            "h-9 w-9 items-center justify-center rounded-full",
            isPink ? "bg-pink/15" : "bg-accent/40"
          )}
        >
          <Ionicons
            name={leadingIcon}
            size={18}
            color={isPink ? "#e879a0" : foreground}
          />
        </View>
      )}

      <View className="flex-1">
        <Text
          className={cn(
            "text-base font-medium",
            isPink ? "text-pink" : "text-foreground"
          )}
        >
          {label}
        </Text>
        {description && (
          <Text className="text-muted-foreground mt-0.5 text-xs">
            {description}
          </Text>
        )}
      </View>

      <View className="ml-auto">
        {trailing !== undefined ? (
          trailing
        ) : onPress ? (
          <Ionicons name="chevron-forward" size={18} color={mutedForeground} />
        ) : null}
      </View>
    </AnimatedPressable>
  );
}
